import { useQuery } from '@apollo/client/react'
import { differenceInCalendarDays } from 'date-fns'
import capitalize from 'lodash/capitalize'

import {
  BufferIcon,
  Button,
  Link,
  LockOpenIcon,
  Notice,
  Skeleton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import {
  useHasOrganizationAction,
  useOrganizationId,
  useOrganizationLimits,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { UnlockChannelDialog } from '~publish/components/UnlockChannelDialog'
import { useCta } from '~publish/hooks/useCta'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import type { JSX } from 'react'

const UPGRADE_CTA = 'channels-pricingNotice-upgradePlan-1'
const UPGRADE_PATH = 'channels-upgrade'

export const ChannelsPricingNoticeQuery = graphql(/* GraphQL */ `
  query ChannelsPricingNotice($input: BillingInput!) {
    billing(input: $input) {
      __typename
      subscription {
        trial {
          endDate
        }
        plan {
          id
          name
        }
      }
      ... on MobileBillingDetails {
        platform
      }
    }
  }
`)

type BillingPlatform = Extract<
  NonNullable<ResultOf<typeof ChannelsPricingNoticeQuery>['billing']>,
  { __typename: 'MobileBillingDetails' }
>['platform']

const MOBILE_APPS: Partial<
  Record<BillingPlatform, { app: string; link: string }>
> = {
  android: {
    app: 'Buffer for Android',
    link: 'https://play.google.com/store/apps/details?id=org.buffer.android',
  },
  apple: {
    app: 'Buffer for iOS',
    link: 'https://apps.apple.com/app/apple-store/id490474324',
  },
}

function getTrialRemainingDays(endDate: string, now: Date): number {
  return Math.max(0, differenceInCalendarDays(new Date(endDate), now))
}

const ChannelsPricingNotice = ({
  numLockedChannels,
  lockedChannelsVisible,
}: {
  numLockedChannels: number
  lockedChannelsVisible: boolean
}): JSX.Element | null => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const canManageBilling = useHasOrganizationAction('manageBilling')
  const openGlobalModal = useOpenGlobalModal()
  const { clicked: upgradeCtaClicked } = useCta(`publish-${UPGRADE_CTA}`, {
    upgradePathName: UPGRADE_PATH,
  })
  const handleUpgrade = (): void => {
    upgradeCtaClicked()
    openGlobalModal(MODALS.planSelector, {
      // The plan selector adds the product prefix to its attribution.
      cta: UPGRADE_CTA,
      upgradePathName: UPGRADE_PATH,
    })
  }
  const organizationId = useOrganizationId()
  const channelLimit = useOrganizationLimits()?.channels
  const { data, loading, error } = useQuery(ChannelsPricingNoticeQuery, {
    variables: { input: { organizationId: organizationId ?? '' } },
    skip: !canManageChannels || !organizationId,
  })

  // `billing(input:)` errors for legacy multi-product organizations, so they
  // get no notice.
  if (!canManageChannels || error) {
    return null
  }

  const billing = data?.billing
  const { plan, trial } = billing?.subscription ?? {}
  const isWebBilling = billing?.__typename === 'WebBillingDetails'
  const mobileApp =
    billing?.__typename === 'MobileBillingDetails'
      ? MOBILE_APPS[billing.platform]
      : undefined
  const showUpgrade =
    canManageBilling && isWebBilling && !trial && plan?.id === 'free'
  const showUnlock =
    isWebBilling && !lockedChannelsVisible && numLockedChannels > 0

  return (
    <Skeleton show={loading}>
      <Notice icon={<BufferIcon />}>
        <Notice.Heading>Get to know your plan</Notice.Heading>
        <Notice.Text>
          {trial ? (
            <>
              You are on a free trial. Your trial ends in{' '}
              <strong>
                {getTrialRemainingDays(trial.endDate, new Date())}{' '}
              </strong>
              days.
            </>
          ) : (
            <>
              You are on the {capitalize(plan?.name ?? 'Free')} plan and can
              connect up to {channelLimit} channel
              {channelLimit === 1 ? '' : 's'}.
            </>
          )}
          {numLockedChannels > 0 && (
            <>
              {' '}
              {numLockedChannels} of your channels
              {numLockedChannels === 1 ? ' is ' : ' are '}
              locked.
            </>
          )}
          {mobileApp && (
            <>
              {' '}
              Please use the{' '}
              <Link
                external
                data-testid="mobile-billing-link"
                href={mobileApp.link}
              >
                {mobileApp.app}
              </Link>{' '}
              app to manage your subscription.
            </>
          )}
        </Notice.Text>
        {(showUpgrade || showUnlock) && (
          <Notice.Actions style={{ flexWrap: 'wrap' }}>
            {showUpgrade && (
              <Button size="small" variant="primary" onClick={handleUpgrade}>
                Upgrade Plan
              </Button>
            )}
            {showUnlock && (
              <>
                <VisuallyHidden id="locked-channels-notice">
                  {numLockedChannels} of your channels are locked.
                </VisuallyHidden>
                <UnlockChannelDialog>
                  <Button
                    size="small"
                    variant="secondary"
                    aria-describedby="locked-channels-notice"
                  >
                    <LockOpenIcon /> Unlock Channels
                  </Button>
                </UnlockChannelDialog>
              </>
            )}
          </Notice.Actions>
        )}
      </Notice>
    </Skeleton>
  )
}

export default ChannelsPricingNotice
